import React, { useState } from 'react';
import { FormField } from './FormField';
import { DollarSignIcon, InfoIcon, LinkIcon, FormInputIcon, TrashIcon, ToggleLeftIcon, ToggleRightIcon, CalendarIcon, ExternalLinkIcon, PlusIcon, ShareIcon, SettingsIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, XIcon, CheckIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { CaptureField, CaptureFieldsEditor } from './CaptureFieldsEditor';
import { ShareLinkModal } from './ShareLinkModal';
interface StaticLink {
  id: string;
  name: string;
  reference: string;
  amount: string;
  currency: string;
  url: string;
  active: boolean;
  createdAt: string;
  usageCount: number;
  totalCollected: number;
  expiryDate?: string; // Optional expiry date and time
}
interface FixedLinksPaymentProps {
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function FixedLinksPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: FixedLinksPaymentProps) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [captureFields, setCaptureFields] = useState<CaptureField[]>([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentLinkToShare, setCurrentLinkToShare] = useState<StaticLink | null>(null);
  // Expiry date and time state
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryTime, setExpiryTime] = useState('23:59');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Sample data for existing static links
  const [existingLinks, setExistingLinks] = useState<StaticLink[]>([{
    id: '1',
    name: 'Monthly Subscription',
    reference: 'monthly-sub',
    amount: '19.99',
    currency: 'USD',
    url: 'https://payment.example.com/pay/monthly-sub',
    active: true,
    createdAt: '2023-05-15',
    usageCount: 47,
    totalCollected: 939.53,
    expiryDate: '2023-12-31 23:59'
  }, {
    id: '2',
    name: 'Premium Plan',
    reference: 'premium-plan',
    amount: '49.99',
    currency: 'USD',
    url: 'https://payment.example.com/pay/premium-plan',
    active: true,
    createdAt: '2023-06-22',
    usageCount: 23,
    totalCollected: 1149.77
  }, {
    id: '3',
    name: 'One-time Donation',
    reference: 'donation-form',
    amount: '10.00',
    currency: 'USD',
    url: 'https://payment.example.com/pay/donation-form',
    active: false,
    createdAt: '2023-08-10',
    usageCount: 156,
    totalCollected: 1560.0,
    expiryDate: '2023-10-15 18:00'
  }]);
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
    if (onAmountChange) {
      onAmountChange(newAmount);
    }
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
    if (onReferenceChange) {
      onReferenceChange(newReference);
    }
  };
  const toggleLinkStatus = (id: string) => {
    setExistingLinks(existingLinks.map(link => link.id === id ? {
      ...link,
      active: !link.active
    } : link));
  };
  const deleteLink = (id: string) => {
    if (confirm('Are you sure you want to permanently delete this payment link?')) {
      setExistingLinks(existingLinks.filter(link => link.id !== id));
    }
  };
  const copyLinkUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      // Could add a toast notification here
      console.log('Link copied to clipboard');
    });
  };
  const handleShareLink = (link: StaticLink) => {
    setCurrentLinkToShare(link);
    setShowShareModal(true);
  };
  // Format date as MM/DD/YYYY
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  // Parse date string to Date object
  const parseDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  // Generate calendar for date picker
  const generateCalendar = () => {
    const today = new Date();
    const currentMonth = expiryDate ? parseDate(expiryDate) : today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        // Fill remaining days of the week
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: currentMonth,
      weeks
    };
  };
  const calendar = generateCalendar();
  const selectedDate = expiryDate ? parseDate(expiryDate) : null;
  const handlePrevMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() - 1);
    // Force re-render
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleNextMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() + 1);
    // Force re-render
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleDateSelect = (date: Date) => {
    if (date) {
      setExpiryDate(formatDate(date));
      setShowDatePicker(false);
    }
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handleCreateLink = () => {
    // In a real implementation, this would create a new link with the form data
    let expiryDateTimeString = undefined;
    if (hasExpiry && expiryDate) {
      // Format the expiry date and time in a readable format
      const [month, day, year] = expiryDate.split('/').map(part => part.trim());
      expiryDateTimeString = `${year}-${month}-${day} ${expiryTime}`;
    }
    const newLink: StaticLink = {
      id: `link-${Date.now()}`,
      name: description || 'New Payment Link',
      reference: reference || `link-${Date.now()}`,
      amount: amount || '0.00',
      currency: currency,
      url: `https://payment.example.com/pay/${reference || Date.now()}`,
      active: true,
      createdAt: new Date().toISOString().split('T')[0],
      usageCount: 0,
      totalCollected: 0,
      expiryDate: expiryDateTimeString
    };
    setExistingLinks([newLink, ...existingLinks]);
    // Reset form fields
    setAmount('');
    setReference('');
    setDescription('');
    setCaptureFields([]);
    setHasExpiry(false);
    setExpiryDate('');
    setExpiryTime('23:59');
  };
  // Format the expiry date for display
  const formatExpiryForDisplay = (expiryString?: string) => {
    if (!expiryString) return 'Never expires';
    // Parse the date string
    const [datePart, timePart] = expiryString.split(' ');
    // Create a readable format
    const date = new Date(datePart + 'T' + timePart);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };
  // Check if a link is expired
  const isLinkExpired = (expiryString?: string) => {
    if (!expiryString) return false;
    const now = new Date();
    const [datePart, timePart] = expiryString.split(' ');
    const expiryDate = new Date(datePart + 'T' + timePart);
    return now > expiryDate;
  };
  return <div className="space-y-6" data-id="element-375">
      {/* Previously created links section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-376">
        <h2 className="text-lg font-medium text-gray-800 mb-6 flex items-center" data-id="element-377">
          <LinkIcon size={18} className="mr-2 text-blue-600" data-id="element-378" />
          Your fixed payment links
        </h2>
        {existingLinks.length > 0 ? <div className="overflow-x-auto" data-id="element-379">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-380">
              <thead className="bg-gray-50" data-id="element-381">
                <tr data-id="element-382">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-383">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-384">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-385">
                    Created
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-386">
                    Expires
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-387">
                    Usage
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-388">
                    Total Collected
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-389">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-390">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-391">
                {existingLinks.map(link => <tr key={link.id} className={!link.active || isLinkExpired(link.expiryDate) ? 'bg-gray-50' : ''} data-id="element-392">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-393">
                      <div className="flex items-center" data-id="element-394">
                        <div data-id="element-395">
                          <div className="text-sm font-medium text-gray-900" data-id="element-396">
                            {link.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-397">
                            {link.reference}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-398">
                      <div className="text-sm text-gray-900" data-id="element-399">
                        ${link.amount} {link.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-400">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-401">
                        <CalendarIcon size={14} className="mr-1 text-gray-400" data-id="element-402" />
                        {link.createdAt}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-403">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-404">
                        <ClockIcon size={14} className="mr-1 text-gray-400" data-id="element-405" />
                        {formatExpiryForDisplay(link.expiryDate)}
                        {isLinkExpired(link.expiryDate) && <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded" data-id="element-406">
                            Expired
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-407">
                      <div className="text-sm text-gray-900" data-id="element-408">
                        {link.usageCount} payments
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-409">
                      <div className="text-sm text-gray-900" data-id="element-410">
                        ${link.totalCollected.toFixed(2)} {link.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-411">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${link.active && !isLinkExpired(link.expiryDate) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-412">
                        {link.active && !isLinkExpired(link.expiryDate) ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-413">
                      <div className="flex items-center justify-end space-x-3" data-id="element-414">
                        <button onClick={() => copyLinkUrl(link.url)} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title="Copy link" data-id="element-415">
                          <ExternalLinkIcon size={16} data-id="element-416" />
                        </button>
                        <button onClick={() => handleShareLink(link)} className="p-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors" title="Share link" data-id="element-417">
                          <ShareIcon size={16} data-id="element-418" />
                        </button>
                        <button onClick={() => toggleLinkStatus(link.id)} className={`p-1.5 rounded-md transition-colors ${link.active ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} title={link.active ? 'Disable link' : 'Enable link'} data-id="element-419">
                          {link.active ? <ToggleRightIcon size={16} data-id="element-420" /> : <ToggleLeftIcon size={16} data-id="element-421" />}
                        </button>
                        <button onClick={() => deleteLink(link.id)} className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition-colors" title="Delete link permanently" data-id="element-422">
                          <TrashIcon size={16} data-id="element-423" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div> : <div className="text-center py-12 bg-gray-50 rounded-md border border-gray-200" data-id="element-424">
            <p className="text-gray-500" data-id="element-425">
              You haven't created any fixed payment links yet
            </p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-426">
              Fill out the form below to create your first fixed payment link
            </p>
          </div>}
      </div>
      {/* Create new payment link form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-427">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-428">
          <PlusIcon size={18} className="mr-2 text-blue-600" data-id="element-429" />
          Create new fixed payment link
        </h2>
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start" data-id="element-430">
          <InfoIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-431" />
          <div data-id="element-432">
            <p className="text-sm text-blue-800" data-id="element-433">
              Fixed links are payment links that can be configured to never
              expire or to expire at a specific date and time. They have a fixed
              amount and can collect customer information before payment.
            </p>
            <p className="text-sm text-blue-800 mt-1" data-id="element-434">
              Perfect for recurring services, donation forms, or any situation
              where you need a consistent payment URL.
            </p>
          </div>
        </div>
        {/* Payment details section */}
        <div className="mb-6" data-id="element-435">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-436">
            <LinkIcon size={16} className="mr-2 text-blue-600" data-id="element-437" />
            Payment details
          </h3>
          <FormField label="Payment amount" required tooltip="The fixed amount your customer will be charged" data-id="element-438">
            <div className="relative" data-id="element-439">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-440">
                <span className="text-gray-500" data-id="element-441">$</span>
              </div>
              <input type="text" className="block w-full pl-8 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-442" />
              <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-443">
                <select className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-md focus:ring-blue-500 focus:border-blue-500" value={currency} onChange={e => handleCurrencyChange(e.target.value)} data-id="element-444">
                  <option data-id="element-445">USD</option>
                  <option data-id="element-446">EUR</option>
                  <option data-id="element-447">GBP</option>
                </select>
              </div>
            </div>
          </FormField>
          <FormField label="Link reference" tooltip="A reference identifier for this payment link" data-id="element-448">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., monthly-subscription or donation-form" value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-449" />
          </FormField>
          <FormField label="Payment description" tooltip="A brief description of what this payment is for" data-id="element-450">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Monthly subscription or Donation" value={description} onChange={e => setDescription(e.target.value)} data-id="element-451" />
          </FormField>
          {/* Expiry Date and Time Field */}
          <div className="mt-4" data-id="element-452">
            <div className="flex items-center mb-2" data-id="element-453">
              <label className="inline-flex items-center cursor-pointer" data-id="element-454">
                <input type="checkbox" className="sr-only" checked={hasExpiry} onChange={() => setHasExpiry(!hasExpiry)} data-id="element-455" />
                <div className={`w-10 h-5 rounded-full transition-colors ${hasExpiry ? 'bg-blue-500' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-456">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${hasExpiry ? 'translate-x-5' : ''}`} data-id="element-457" />
                </div>
                <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-458">
                  Set expiration date and time
                </span>
              </label>
              <InfoTooltip content="By default, fixed links never expire. Enable this option to set a specific expiration date and time." data-id="element-459" />
            </div>
            {hasExpiry && <div className="mt-2 grid grid-cols-2 gap-4" data-id="element-460">
                <div className="relative" data-id="element-461">
                  <FormField label="Expiry date" tooltip="The date when this payment link will expire" data-id="element-462">
                    <div className="relative" data-id="element-463">
                      <input type="text" className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" value={expiryDate} onClick={() => setShowDatePicker(!showDatePicker)} readOnly data-id="element-464" />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-465">
                        <CalendarIcon size={16} className="text-gray-400" data-id="element-466" />
                      </div>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-467">
                        {showDatePicker ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-468" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-469" />}
                      </div>
                    </div>
                    {/* Date Picker Dropdown */}
                    {showDatePicker && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 w-full" data-id="element-470">
                        <div className="flex justify-between items-center mb-2" data-id="element-471">
                          <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handlePrevMonth} data-id="element-472">
                            <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-473" />
                          </button>
                          <div className="font-medium" data-id="element-474">
                            {calendar.month.toLocaleString('default', {
                        month: 'long',
                        year: 'numeric'
                      })}
                          </div>
                          <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handleNextMonth} data-id="element-475">
                            <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-476" />
                          </button>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-477">
                          <div className="text-gray-500" data-id="element-478">Su</div>
                          <div className="text-gray-500" data-id="element-479">Mo</div>
                          <div className="text-gray-500" data-id="element-480">Tu</div>
                          <div className="text-gray-500" data-id="element-481">We</div>
                          <div className="text-gray-500" data-id="element-482">Th</div>
                          <div className="text-gray-500" data-id="element-483">Fr</div>
                          <div className="text-gray-500" data-id="element-484">Sa</div>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center" data-id="element-485">
                          {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                    w-8 h-8 rounded-full flex items-center justify-center text-sm
                                    ${isSelected(date) ? 'bg-blue-600 text-white' : ''}
                                    ${isToday(date) && !isSelected(date) ? 'border border-blue-600 text-blue-600' : ''}
                                    ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                                  `} onClick={() => handleDateSelect(date)} data-id="element-486">
                                  {date.getDate()}
                                </button> : <div key={index} className="w-8 h-8" data-id="element-487"></div>))}
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-488">
                          <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                      const date = new Date();
                      date.setDate(date.getDate() + 7);
                      setExpiryDate(formatDate(date));
                      setShowDatePicker(false);
                    }} data-id="element-489">
                            7 days
                          </button>
                          <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                      const date = new Date();
                      date.setDate(date.getDate() + 30);
                      setExpiryDate(formatDate(date));
                      setShowDatePicker(false);
                    }} data-id="element-490">
                            30 days
                          </button>
                          <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                      const date = new Date();
                      date.setDate(date.getDate() + 90);
                      setExpiryDate(formatDate(date));
                      setShowDatePicker(false);
                    }} data-id="element-491">
                            90 days
                          </button>
                        </div>
                      </div>}
                  </FormField>
                </div>
                <FormField label="Expiry time" tooltip="The time when this payment link will expire (merchant local time)" data-id="element-492">
                  <div className="relative" data-id="element-493">
                    <input type="time" className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={expiryTime} onChange={e => setExpiryTime(e.target.value)} data-id="element-494" />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-495">
                      <ClockIcon size={16} className="text-gray-400" data-id="element-496" />
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500" data-id="element-497">
                    Time is in your local timezone (
                    {Intl.DateTimeFormat().resolvedOptions().timeZone})
                  </div>
                </FormField>
              </div>}
          </div>
        </div>
        {/* Customer information section */}
        <div className="mb-6" data-id="element-498">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-499">
            <FormInputIcon size={16} className="mr-2 text-blue-600" data-id="element-500" />
            Customer information
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-501">
            Specify what information you want to collect from customers before
            they complete their payment.
          </p>
          <CaptureFieldsEditor fields={captureFields} onChange={setCaptureFields} data-id="element-502" />
        </div>
        {/* Metadata section - using the common component but only for metadata */}
        <div className="mb-6" data-id="element-503">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-504">
            <SettingsIcon size={16} className="mr-2 text-blue-600" data-id="element-505" />
            Additional options
          </h3>
          <div className="bg-gray-50 rounded-md p-4" data-id="element-506">
            {/* Using only the metadata and redirect functionality from CommonPaymentOptions */}
            <FormField label="Success redirect URL" tooltip="Where to redirect customers after successful payment" data-id="element-507">
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="https://example.com/thank-you" value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)} data-id="element-508" />
            </FormField>
            {/* Metadata editor directly embedded */}
            <div className="mt-4" data-id="element-509">
              <FormField label="Custom metadata" tooltip="Add additional data to track with this payment" data-id="element-510">
                <div className="mt-2" data-id="element-511">
                  <button type="button" onClick={() => setMetadata([...metadata, {
                  key: '',
                  value: ''
                }])} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-512">
                    <PlusIcon size={16} className="mr-1" data-id="element-513" />
                    Add metadata field
                  </button>
                </div>
                {metadata.length > 0 && <div className="mt-3 space-y-3" data-id="element-514">
                    {metadata.map((item, index) => <div key={index} className="flex space-x-2" data-id="element-515">
                        <input type="text" className="block w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Key" value={item.key} onChange={e => {
                    const updatedMetadata = [...metadata];
                    updatedMetadata[index].key = e.target.value;
                    setMetadata(updatedMetadata);
                  }} data-id="element-516" />
                        <input type="text" className="block w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Value" value={item.value} onChange={e => {
                    const updatedMetadata = [...metadata];
                    updatedMetadata[index].value = e.target.value;
                    setMetadata(updatedMetadata);
                  }} data-id="element-517" />
                        <button type="button" onClick={() => {
                    const updatedMetadata = [...metadata];
                    updatedMetadata.splice(index, 1);
                    setMetadata(updatedMetadata);
                  }} className="p-2 text-gray-400 hover:text-red-500" data-id="element-518">
                          <TrashIcon size={18} data-id="element-519" />
                        </button>
                      </div>)}
                  </div>}
              </FormField>
            </div>
          </div>
        </div>
        {/* Create link button */}
        <div className="flex justify-end" data-id="element-520">
          <button onClick={handleCreateLink} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md flex items-center transition-colors" data-id="element-521">
            <PlusIcon size={16} className="mr-2" data-id="element-522" />
            Create payment link
          </button>
        </div>
      </div>
      {/* Share Link Modal */}
      {showShareModal && <ShareLinkModal onClose={() => setShowShareModal(false)} recipients={[]} data-id="element-523" />}
    </div>;
}
// Helper component for the info tooltip
function InfoTooltip({
  content
}: {
  content: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  return <div className="relative ml-1.5" data-id="element-524">
      <div className="text-gray-400 hover:text-gray-500 cursor-help" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} data-id="element-525">
        <InfoIcon size={14} data-id="element-526" />
      </div>
      {showTooltip && <div className="absolute z-10 w-64 px-3 py-2 text-sm text-left font-normal text-gray-700 bg-white border border-gray-200 rounded-md shadow-lg -top-2 left-6" data-id="element-527">
          {content}
        </div>}
    </div>;
}